import Dexie, { Table } from "dexie";
import { IActivity } from "../types";
import { canUseIndexedDB } from './consent';

export interface Audio {
  activityId: string;
  activityType: string;
  description: string;
  uploaded: boolean;
  audio: ArrayBuffer | null;
  week: Date; // 2022-02-21
  recordingOrder?: number;
  createdAt?: string;
}

export interface Activity extends IActivity {}

export class AppDexie extends Dexie {
  audios!: Table<Audio>;
  activities!: Table<Activity>;
  constructor() {
    super("speechbox_db");
    this.version(1).stores({
      audios: "activityId", // Primary key and indexed props
      activities: "activityId",
    });
  }
}

export const db = new AppDexie();

// Get the current consent state
const getConsentState = () => {
  try {
    const storedConsent = localStorage.getItem('speechbox_cookie_consent');
    if (storedConsent) {
      return JSON.parse(storedConsent);
    }
    // Default to allowing essential only if no consent stored
    return { essential: true, analytics: false, preferences: false };
  } catch (error) {
    console.error('Error reading consent state:', error);
    // Default to essential only on error
    return { essential: true, analytics: false, preferences: false };
  }
};

export const dbSaveAudio = async (payload: Audio) => {
  const now = new Date().toISOString();
  payload.createdAt = payload.createdAt ?? now;

  
  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    throw new Error('Storage consent not provided');
  }

  const found = await db.audios.get({ activityId: payload.activityId });
  if (found) {
    await db.audios.delete(found.activityId);
  }
  
  const result = await db.audios.add(payload);  
  return result;
};

export const dbBulkUpsertActivities = async (payload: Activity[]) => {
  console.log("dbBulkUpsertActivities..2................",payload);

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    throw new Error('Storage consent not provided');
  }

  // see if the activity is there, if no insert
  for (const activity of payload) {
    const present = await db.activities.get({
      activityId: activity.activityId,
    });
    if (!present) {
      await db.activities.add(activity);
    }
  }
  return true;
};

export const dbGetActivity = async (activityId: string) => {
  console.log("dbGetActivity..3................",activityId);

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    throw new Error('Storage consent not provided');
  }

  return db.activities.get({ activityId });
};

export const dbGetNextToRecord = async () => {
  console.log("dbGetNextToRecord..4................");

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    throw new Error('Storage consent not provided');
  }

  // check all recordings done
  const saved = await db.audios.toArray();
  const savedActivityIds = saved.map((s) => s.activityId);

  const allActivities = await db.activities.toArray();
  // Dexie's toArray returns results in primary-key order (activityId), which
  // does not necessarily match the upload/created order. We need to sort by
  // createdAt to preserve the order in which admin uploaded activities.
  const sortedActivities = allActivities.sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return ta - tb;
  });

  const allActivityIds = sortedActivities.map((a) => a.activityId);

  const rem = allActivityIds.filter((a) => !savedActivityIds.includes(a));
  if (rem.length) {
    return rem[0];
  }
  return "";
};

export const dbSavedCount = async () => {
  console.log("dbSavedCount..5................");

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    return 0; // Return 0 if no consent
  }

  const saved = await db.audios.toArray();
  console.log("dbSavedCount..................",saved);
  return saved.length;
};

export const dbSavedAudios = async () => {
  console.log("dbSavedAudios.6................");

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    return []; // Return empty array if no consent
  }

  const arr = await db.audios.toArray();
  const sorted = arr.sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return ta - tb;
  });
  return sorted;
};

export const dbNuke = async () => {
  console.log("dbNuke....7..............");

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    return; // Return without action if no consent
  }

  await db.audios.clear();
  await db.activities.clear();
  return;
};

export const db_audioNuke = async () => {
  console.log("db_audioNuke...8...............");

  const consentState = getConsentState();
  if (!canUseIndexedDB(consentState)) {
    console.warn('IndexedDB access denied due to lack of consent');
    return; // Return without action if no consent
  }

  await db.audios.clear();
  return;
};